import { Profile } from '@app/models/account/profile';
import { DivisionConfiguration } from '@app/models/ebo/shared/division-configuration';
import { EboRoles } from '@app/models/ebo/shared/policies/ebo-roles.enum';
import { assignSafe, Policy } from '@summit/core';
import { DayOfWeek, IUserExperience, SummitMenuItem, ViewChoices } from '@summit/models';

import { NavMenuItem } from '@app/config/nav-menu-item';
import { WageProfile } from '@app/models/account/wage-profile';
import { PayrollFormat } from '../tenant/payroll-format.enum';
import { DigitalLogParameter } from './policies/digital-log-parameter';
import { PolicyToPermissionsMapping } from './policies/ebo-permissions.enum';

export class UserExperience implements IUserExperience {
  defaultView: ViewChoices = 'normal';
  // menuBarSelection: MenuItemEBO = null;
  menuBarSelection: SummitMenuItem<NavMenuItem> | undefined | null = null;
  activeEmployeeID: string;
  activeEmployee: Profile;
  roles: EboRoles;
  managedDivisionIds: string[]; // IDs of divisions managed by this user
  wage: WageProfile;
  divisionConfig: DivisionConfiguration;

  // account policies
  systemAccount = false;
  // tenant Polices
  endOfWeek: DayOfWeek;
  endOfScheduleWeek: DayOfWeek;
  endOfIvmrWeek: DayOfWeek;
  overrideReconcileLock: boolean;
  // division policies
  //digitalLogParameters: DigitalLogParameter;

  //TBD Polices
  payrollFormat: PayrollFormat;
  delegatee: boolean;

  constructor(jsonData?: Partial<UserExperience>) {
    if (jsonData) {
      //console.trace(`Current User Experience : `, jsonData);
      //console.log(`Current User has roles: ${jsonData.roles} of ${JSON.stringify(jsonData, null, 2)}`);
      assignSafe(this, jsonData);
      if (jsonData.menuBarSelection) this.menuBarSelection = jsonData.menuBarSelection;
      // if (jsonData.menuBarSelection) this.menuBarSelection = new MenuItemEBO(jsonData.menuBarSelection);
      if (jsonData.activeEmployee) this.activeEmployee = new Profile(jsonData.activeEmployee);
      if (jsonData.roles) this.roles = jsonData.roles;
      // if (jsonData.digitalLogParameters)
      //   this.digitalLogParameters = new DigitalLogParameter(jsonData.digitalLogParameters);
      if (jsonData.divisionConfig) {
        //console.log(`Ux creating Division Configuration Report Parameters:`, jsonData.divisionConfig);
        this.divisionConfig = new DivisionConfiguration(jsonData.divisionConfig);
      }
    }
    this.endOfScheduleWeek ??= this.endOfWeek;
    this.endOfIvmrWeek ??= DayOfWeek.Saturday;
    if (this.isDriverOnly()) {
      this.defaultView === 'employee';
    }
    this.roles ??= EboRoles.None;
    this.payrollFormat ??= PayrollFormat.None;
    //console.log(`Current User Experience : `, this);
  }

  getDigitalLogParameters(): DigitalLogParameter {
    return this.divisionConfig?.employeeParameters?.digitalLogParameters;
  }
  get isNotBackOffice() {
    return (this.roles & (EboRoles.BackOffice | EboRoles.Admin)) === 0;
  }
  get schedulesEnabled() {
    return (
      (this.divisionConfig?.employeeParameters?.scheduleParameters?.schedulingEnabled ?? false) &&
      (this?.activeEmployee.features.scheduler ?? false)
    );
  }
  get driverLogsEnabled() {
    return this?.activeEmployee?.features.driverLogs ?? false;
  }
  isLoggedIn(): boolean {
    let response: boolean;
    if (this.activeEmployeeID && this.activeEmployeeID.length > 0) {
      response = true;
    } else {
      response = false;
    }
    // console.log(`User is Logged In: ${response}`, this.activeEmployeeID);
    return response;
  }
  /* Features */
  isSchedulerEnabled(): boolean {
    return this.activeEmployee?.features?.scheduler;
  }
  isDigitalLogsEnabled(): boolean {
    return this.activeEmployee?.features?.driverLogs;
  }
  isPayrollEnabled(): boolean {
    return this.activeEmployee?.features?.payroll ?? false;
  }

  //Access currently is their direct permissions
  //Report is for the division permissions
  canAccessFinancials(): boolean {
    return this.activeEmployee?.canAccessFinancials();
  }

  canAccessDisputes(): boolean {
    return this.activeEmployee?.canAccessDisputes();
  }

  canAccessLH(): boolean {
    return this.activeEmployee?.canAccessLH();
  }
  canReportLH(): boolean {
    return this.activeEmployee?.canReportLH();
  }
  canAccessHourly(): boolean {
    return this.activeEmployee?.canAccessHourly();
  }
  canReportHourly(): boolean {
    return this.activeEmployee?.canReportHourly();
  }
  canAccessSpots(): boolean {
    return this.activeEmployee?.canAccessSpots();
  }
  canReportSpots(): boolean {
    return this.activeEmployee?.canReportSpots();
  }
  canAccessExpense(): boolean {
    return this.activeEmployee?.canAccessExpense();
  }
  canReportExpense(): boolean {
    return this.activeEmployee?.canReportExpense();
  }
  canAccessMiscWork(): boolean {
    return this.activeEmployee?.canAccessMiscWork();
  }
  canReportMiscWork(): boolean {
    return this.activeEmployee?.canReportMiscWork();
  }
  canAccessPrePostTrip(): boolean {
    return this.activeEmployee?.canAccessPrePostTrip();
  }
  canAccessTimeClock(): boolean {
    return this.activeEmployee?.canAccessTimeClock();
  }
  canReportPrePostTrip(): boolean {
    return this.activeEmployee?.canReportPrePostTrip();
  }

  canReportSickTime(): boolean {
    return this.activeEmployee?.canReportSickTime();
  }
  canReportVacation(): boolean {
    return this.activeEmployee?.canReportVacation();
  }
  canReportHoliday(): boolean {
    return this.activeEmployee?.canReportHoliday();
  }
  canReportAdvances(): boolean {
    return this.activeEmployee?.canReportAdvances();
  }

  canAccessScheduling(): boolean {
    return this.activeEmployee?.canAccessScheduling();
  }
  canReportScheduling(): boolean {
    return this.activeEmployee?.canReportScheduling();
  }

  canEditTimeOff(): boolean {
    return this.activeEmployee?.canEditTimeOff();
  }

  canEditRouteInfo(): boolean {
    return this.activeEmployee?.canEditRouteInfo();
  }

  getCompanyName(): string {
    return this.activeEmployee?.tenant?.defaultCompany?.name;
  }
  get showCompanySelection(): boolean {
    return this.activeEmployee?.showCompanySelection();
  }
  /* Roles */
  isSysAdmin(): boolean {
    if (this.roles && this.hasRole(EboRoles.SysAdmin)) {
      return true;
    }
    return false;
  }
  isSysAccount(): boolean {
    //console.log(`Is Sys Account is returning: ${this.systemAccount}`)
    return this.systemAccount;
  }
  isAdmin(): boolean {
    return this.activeEmployee?.isAdmin() ?? false;
  }
  isPayroll(): boolean {
    return this.activeEmployee?.isPayroll() ?? false;
  }
  isBackOffice(): boolean {
    return this.activeEmployee?.isBackOffice() ?? false;
  }
  isManager(): boolean {
    return this.activeEmployee?.isManager() ?? false;
  }
  isManagerOnly(): boolean {
    return this.activeEmployee?.isManagerOnly() ?? false;
  }
  isLeadDriver(): boolean {
    return this.activeEmployee?.isLeadDriver() ?? false;
  }
  isMechanic(): boolean {
    return this.activeEmployee?.isMechanic() ?? false;
  }

  isDriverOnly(): boolean {
    return this.activeEmployee?.isDriverOnly() ?? true;
  }

  isDelegatee(): boolean {
    return this.delegatee;
  }

  showPayroll(): boolean {
    return (
      this.isPayroll() ||
      (this.isBackOffice() && this.divisionConfig?.employeeParameters.payRollParameters.backOfficeAssistsWithPayroll) ||
      (this.isManager() && this.divisionConfig?.employeeParameters.payRollParameters.managerAssistsWithPayroll)
    );
  }

  hasRole(flag: EboRoles): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.roles & flag);
  }

  hasPermissions(levelNeeded: Policy): boolean {
    switch (levelNeeded) {
      case Policy.Any:
        return true;
      case Policy.Manager:
        return this.isManager();
      case Policy.BackOffice:
        return this.isBackOffice();
      case Policy.Payroll:
        return this.isPayroll();
      case Policy.Admin:
        return this.isAdmin();
      case Policy.SysAdmin:
        return this.isSysAdmin();
      default:
        return this.roles & PolicyToPermissionsMapping[levelNeeded] ? true : false;
    }
  }

  getTimeZone(): string {
    return this.activeEmployee?.tenant?.getTimeZone();
  }
}
